import clsx from "clsx";
import React, {useState} from "react";
import {sameOriginArtifactUrl} from "@/utils/artifactUrl";

const InlineDocPreview: React.FC<{url: string}> = ({url}) => {
   const containerRef = React.useRef<HTMLDivElement>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   React.useEffect(() => {
      const loadDocx = async () => {
         try {
            const {renderAsync} = await import("docx-preview");
            // Wingman URLs ride the same-origin signed-in rewrite; see
            // `src/utils/artifactUrl.ts`.
            const response = await fetch(sameOriginArtifactUrl(url), {credentials: "same-origin"});
            const blob = await response.blob();
            if (containerRef.current) {
               containerRef.current.innerHTML = "";
               await renderAsync(blob, containerRef.current, undefined, {
                  className: "docx-preview",
                  inWrapper: true,
                  ignoreWidth: false,
                  ignoreHeight: true,
               });
            }
         } catch (err) {
            console.error("DOCX render error:", err);
            setError("Failed to load document");
         } finally {
            setLoading(false);
         }
      };
      loadDocx();
   }, [url]);

   if (error) {
      return (
         <div className="flex items-center justify-center h-full text-500 text-sm">{error}</div>
      );
   }

   return (
      <div className="relative w-full h-full">
         {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-100">
               <div className="w-8 h-8 border-2 border-300 border-t-primary rounded-full animate-spin" />
            </div>
         )}
         <div
            ref={containerRef}
            className={clsx(
               "w-full h-full overflow-auto bg-100",
               "[&_.docx-wrapper]:bg-100 [&_.docx-wrapper]:p-4 [&_.docx-wrapper]:flex [&_.docx-wrapper]:flex-col [&_.docx-wrapper]:items-center [&_.docx-wrapper]:gap-4",
               "[&_.docx-wrapper>section]:shadow-lg [&_.docx-wrapper>section]:bg-panel [&_.docx-wrapper>section]:max-w-[600px] [&_.docx-wrapper>section]:w-full [&_.docx-wrapper>section]:mx-auto [&_.docx-wrapper>section]:overflow-hidden"
            )}
         />
      </div>
   );
};

export default InlineDocPreview;
